import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Handshake, Inbox, Send, Loader2, User, Building2, CheckCircle2,
  XCircle, ArrowUpRight, MessageSquare, Sparkles,
} from "lucide-react";
import Seo from "../components/Seo";
import AppLayout from "../components/AppLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: "New", cls: "bg-[color:var(--brand-teal)]/10 text-[color:var(--brand-teal)]" },
  viewed: { label: "Viewed", cls: "bg-[color:var(--soft)] text-[color:var(--ink)]" },
  accepted: { label: "Accepted", cls: "bg-[color:var(--brand-green)]/10 text-[color:var(--brand-green)]" },
  declined: { label: "Declined", cls: "bg-[color:var(--brand-red)]/10 text-[color:var(--brand-red)]" },
};

function StatusPill({ status }) {
  const s = STATUS_META[status] || STATUS_META.new;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${s.cls}`}>
      {s.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

function OtherAvatar({ other }) {
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[color:var(--soft)]">
      {other?.profile_photo
        ? <img src={other.profile_photo} alt="" className="h-full w-full object-cover" />
        : <User className="h-5 w-5 text-[color:var(--ink-soft)]" />}
    </div>
  );
}

function TargetSummary({ item }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="font-display truncate text-base font-semibold text-[color:var(--ink)]">
        {item.other?.name || "Hub member"}
      </p>
      <p className="truncate text-xs text-[color:var(--ink-soft)]">
        {item.other?.professional_headline || item.other?.member_type || ""}
      </p>
      {item.target_business?.name && (
        <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-[color:var(--soft)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--ink)]">
          <Building2 className="h-3 w-3" /> {item.target_business.name}
        </p>
      )}
    </div>
  );
}

function ReceivedCard({ item, onPatch, busy }) {
  const done = item.status === "accepted" || item.status === "declined";
  return (
    <div className="hub-card rounded-2xl p-5" data-testid={`interest-received-${item.id}`}>
      <div className="flex items-start gap-4">
        <OtherAvatar other={item.other} />
        <TargetSummary item={item} />
        <StatusPill status={item.status} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--soft)] px-2.5 py-1 text-[11px] font-semibold text-[color:var(--ink)]">
          <Handshake className="h-3 w-3" /> {item.interest_type}
        </span>
        <span className="text-[11px] text-[color:var(--ink-soft)]">{formatDate(item.created_at)}</span>
      </div>

      {item.message && (
        <div className="mt-3 rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4">
          <p className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">
            <MessageSquare className="h-3 w-3" /> Message
          </p>
          <p className="mt-1 text-sm text-[color:var(--ink)] whitespace-pre-line">{item.message}</p>
        </div>
      )}

      {item.status === "accepted" && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-[color:var(--brand-green)]/30 bg-[color:var(--brand-green)]/5 p-3 text-sm text-[color:var(--brand-green)]">
          <Sparkles className="h-4 w-4" /> Connection accepted — you can continue the conversation from their profile.
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        {item.other?.user_id && (
          <Link to={`/app/professionals/${item.other.user_id}`} data-testid={`interest-view-profile-${item.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-4 py-2 text-xs font-semibold text-[color:var(--ink)] hover:border-[color:var(--ink)]">
            View profile <ArrowUpRight className="h-3 w-3" />
          </Link>
        )}
        {!done && (
          <>
            <button
              disabled={busy}
              onClick={() => onPatch(item.id, "declined")}
              data-testid={`interest-decline-${item.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-[color:var(--brand-red)]/40 bg-[color:var(--brand-red)]/5 px-4 py-2 text-xs font-semibold text-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/10 disabled:opacity-60"
            >
              <XCircle className="h-3.5 w-3.5" /> Decline
            </button>
            <button
              disabled={busy}
              onClick={() => onPatch(item.id, "accepted")}
              data-testid={`interest-accept-${item.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-[color:var(--invert-bg)] px-4 py-2 text-xs font-semibold text-white hover:-translate-y-0.5 transition-transform disabled:opacity-60"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Accept
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SentCard({ item }) {
  return (
    <div className="hub-card rounded-2xl p-5" data-testid={`interest-sent-${item.id}`}>
      <div className="flex items-start gap-4">
        <OtherAvatar other={item.other} />
        <TargetSummary item={item} />
        <StatusPill status={item.status} />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--soft)] px-2.5 py-1 text-[11px] font-semibold text-[color:var(--ink)]">
          <Handshake className="h-3 w-3" /> {item.interest_type}
        </span>
        <span className="text-[11px] text-[color:var(--ink-soft)]">Sent {formatDate(item.created_at)}</span>
      </div>
      {item.message && (
        <div className="mt-3 rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">Your message</p>
          <p className="mt-1 text-sm text-[color:var(--ink)] whitespace-pre-line">{item.message}</p>
        </div>
      )}
      {item.status === "accepted" && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-[color:var(--brand-green)]/30 bg-[color:var(--brand-green)]/5 p-3 text-sm text-[color:var(--brand-green)]">
          <Sparkles className="h-4 w-4" /> Connection accepted.
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, title, sub }) {
  return (
    <div className="mt-8 rounded-2xl border border-dashed border-[color:var(--hairline)] bg-[color:var(--surface)] p-10 text-center">
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--soft)] text-[color:var(--ink-soft)]">
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="font-display mt-4 text-lg font-semibold text-[color:var(--ink)]">{title}</h3>
      <p className="mt-2 text-sm text-[color:var(--ink-soft)]">{sub}</p>
    </div>
  );
}

export default function Interests() {
  const [tab, setTab] = useState("received");
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/hub/interests/received`, { withCredentials: true }),
        axios.get(`${API}/hub/interests/sent`, { withCredentials: true }),
      ]);
      setReceived(r.data.items || []);
      setSent(s.data.items || []);
    } catch { toast.error("Failed to load interests."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/hub/interests/${id}`, { status }, { withCredentials: true });
      toast.success(status === "accepted" ? "Connection accepted." : "Interest declined.");
      // Optimistic update
      setReceived((prev) => prev.map((i) => i.id === id ? { ...i, status } : i));
    } catch { toast.error("Could not update."); }
    finally { setBusy(false); }
  };

  const pendingCount = received.filter((i) => i.status === "new" || i.status === "viewed").length;

  return (
    <AppLayout>
      <Seo title="Interests" description="Manage the professional interests sent to you and by you across StUpEco Hub." path="/app/interests" />
      <div className="container-hub py-8 lg:py-10" data-testid="interests-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--brand-teal)]">
              <Handshake className="h-3.5 w-3.5" /> Connect
            </p>
            <h1 className="font-display mt-2 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">
              Your interests
            </h1>
            <p className="mt-2 max-w-xl text-sm text-[color:var(--ink-soft)]">
              Every connection on the Hub starts with a clear business intent — partnership, cross-selling,
              investment, mentorship or more. Manage them all here.
            </p>
          </div>
        </div>

        <div className="mt-8 inline-flex rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] p-1" data-testid="interests-tabs">
          <button
            onClick={() => setTab("received")}
            data-testid="interests-tab-received"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "received" ? "bg-[color:var(--invert-bg)] text-white" : "text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"
            }`}
          >
            <Inbox className="h-4 w-4" /> Received
            {pendingCount > 0 && (
              <span data-testid="interests-received-badge"
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${tab === "received" ? "bg-white text-[color:var(--invert-bg)]" : "bg-[color:var(--brand-teal)] text-white"}`}>
                {pendingCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("sent")}
            data-testid="interests-tab-sent"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "sent" ? "bg-[color:var(--invert-bg)] text-white" : "text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"
            }`}
          >
            <Send className="h-4 w-4" /> Sent
          </button>
        </div>

        {loading ? (
          <div className="mt-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div>
        ) : tab === "received" ? (
          received.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No interests yet."
              sub="When another member expresses interest in you or your business, it'll appear here."
            />
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="interests-received-list">
              {received.map((i) => <ReceivedCard key={i.id} item={i} onPatch={patch} busy={busy} />)}
            </div>
          )
        ) : (
          sent.length === 0 ? (
            <EmptyState
              icon={Send}
              title="You haven't sent any interests yet."
              sub={
                <>
                  Start with <Link to="/app/businesses" className="underline">Businesses</Link> or{" "}
                  <Link to="/app/professionals" className="underline">Professionals</Link> and press
                  Express Interest.
                </>
              }
            />
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="interests-sent-list">
              {sent.map((i) => <SentCard key={i.id} item={i} />)}
            </div>
          )
        )}
      </div>
    </AppLayout>
  );
}
